// src/components/PDFViewer.js
import React, { useEffect, useState } from 'react';
import { CircularProgress, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton } from '@material-ui/core';
import { ChevronLeft, ChevronRight, CloudDownload } from '@material-ui/icons';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { appUrl } from '../../../../../../../../utils/config';

export const PDFViewer = ({ schrockenCustomFileID, onFileTypeChange }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [fileURL, setFileURL] = useState(null);
    const [excelData, setExcelData] = useState(null);
    const [showPreview, setShowPreview] = useState(true);
    const [fileType, setFileType] = useState(null);
    const [fileName, setFileName] = useState('');
    const [page, setPage] = useState(0); // Move the pagination state here, at the top level

    useEffect(() => {
        let url = null;
        if (schrockenCustomFileID) {
            const data = {
                schrockenCustomFileID,
            };
            axios
                .post(
                    `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/downloadCustomFile`,
                    data
                )
                .then((res) => {
                    if (res.data.status) {
                        const fileDataArray = res.data.fileData.data;
                        const byteArray = new Uint8Array(fileDataArray);
                        
                        // Determine file type by checking the filename
                        const fname = res.data.fileName || '';
                        setFileName(fname);
                        
                        if (fname.toLowerCase().endsWith('.pdf')) {
                            // Handle PDF - using the original efficient PDF viewer code
                            const newFileType = 'pdf';
                            setFileType(newFileType);
                            // Notify parent component if callback exists
                            if (onFileTypeChange) onFileTypeChange(newFileType);
                            
                            const blob = new Blob([byteArray], {
                                type: 'application/pdf',
                            });
                            url = URL.createObjectURL(blob);
                            setFileURL(url);
                            // PDF loading is optimized to be as efficient as the original code
                        } else if (fname.toLowerCase().endsWith('.xlsx') || fname.toLowerCase().endsWith('.xls')) {
                            // Handle Excel files
                            const newFileType = 'excel';
                            setFileType(newFileType);
                            // Notify parent component if callback exists
                            if (onFileTypeChange) onFileTypeChange(newFileType);
                            
                            const blob = new Blob([byteArray], {
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            });
                            url = URL.createObjectURL(blob);
                            setFileURL(url);
                            
                            // Parse Excel data for preview
                            try {
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                    const data = new Uint8Array(e.target.result);
                                    const workbook = XLSX.read(data, { type: 'array' });
                                    const firstSheetName = workbook.SheetNames[0];
                                    const worksheet = workbook.Sheets[firstSheetName];
                                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                                    setExcelData(jsonData);
                                };
                                reader.readAsArrayBuffer(blob);
                            } catch (error) {
                                console.error('Error parsing Excel:', error);
                            }
                        }
                    }
                })
                .catch((err) => {
                    console.error('Error loading file:', err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }

        return () => {
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [schrockenCustomFileID]);

    // For PDF files - using the original efficient approach directly in the return statement
    if (fileType === 'pdf') {
        return (
            <Box position="relative" height="800px" border={1} borderColor="grey.300" borderRadius={8} overflow="hidden">
                {isLoading && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bgcolor="rgba(255, 255, 255, 0.75)"
                    >
                        <CircularProgress color="primary" />
                    </Box>
                )}
                {fileURL && (
                    <iframe
                        src={fileURL}
                        style={{ width: '100%', height: '100%' }}
                        title="PDF Viewer"
                        onLoad={() => setIsLoading(false)}
                    />
                )}
            </Box>
        );
    }

    // For Excel files
    const rowsPerPage = 50; // Adjust as needed

    if (fileType === 'excel') {
        const totalRows = excelData ? excelData.length - 1 : 0;
        const totalPages = Math.ceil(totalRows / rowsPerPage);

        const handlePrevPage = () => setPage((prev) => Math.max(prev - 1, 0));
        const handleNextPage = () => setPage((prev) => Math.min(prev + 1, totalPages - 1));

        return (
            <Box 
                position="relative" 
                border={1} 
                borderColor="grey.300" 
                borderRadius={8}
                p={3}
                style={{backgroundColor: '#fff'}}
            >
                {isLoading ? (
                    <Box display="flex" justifyContent="center" height="200px" alignItems="center">
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <>
                        <Box style={{padding: 0}} display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Box display="flex" alignItems="center">
                                <img 
                                    src="https://img.icons8.com/color/96/000000/microsoft-excel-2019--v1.png" 
                                    alt="Excel file" 
                                    style={{ width: '48px', height: '48px', marginRight: '16px' }}
                                />
                                <Box>
                                    <h3 style={{ margin: 0 }}>Excel Spreadsheet</h3>
                                    <p style={{ margin: 0, color: '#666' }}>Preview</p>
                                </Box>
                            </Box>
                            <Box>
                                {fileURL && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CloudDownload />}
                                        href={fileURL}
                                        download={fileName || "excel-document.xlsx"}
                                        style={{ marginRight: '8px' }}
                                    >
                                        Download
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        
                        {showPreview && excelData && (
                            <>
                                <TableContainer component={Paper} style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                {excelData[0] && excelData[0].map((cell, i) => (
                                                    <TableCell key={i} style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                        {cell}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {excelData
                                                .slice(1 + page * rowsPerPage, 1 + (page + 1) * rowsPerPage)
                                                .map((row, i) => (
                                                    <TableRow key={i + page * rowsPerPage}>
                                                        {row.map((cell, j) => (
                                                            <TableCell key={j}>{cell}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {totalPages > 0 && (
                                    <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                                         <IconButton
                                                      size="small"
                                                      onClick={handlePrevPage}
                                                      disabled={page === 0} style={{ marginRight: 8 }}
                                                    >
                                                      <ChevronLeft fontSize="small" />
                                                    </IconButton>
                                        <span>
                                            Page {page + 1} of {totalPages}
                                        </span>
                                        <IconButton
                                                      size="small"
                                                      onClick={handleNextPage}
                                                      disabled={page >= totalPages - 1} style={{ marginLeft: 8 }}
                                                    >
                                                      <ChevronRight fontSize="small" />
                                                    </IconButton>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
        );
    }

    // Loading state or unsupported file type
    return (
        <Box display="flex" justifyContent="center" height="200px" alignItems="center">
            {isLoading ? (
                <CircularProgress color="primary" />
            ) : (
                <Box p={3}>Unsupported file type</Box>
            )}
        </Box>
    );
};
